// src/App.js
import React, { Suspense, lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import theme from "./styles/theme";
import GlobalStyles from "./styles/GlobalStyles";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";

import Home from "./routes/Home";

const Products = lazy(() => import("./routes/Products"));
const ProductDetail = lazy(() => import("./routes/ProductDetail"));
const About = lazy(() => import("./routes/About"));
const Contact = lazy(() => import("./routes/Contact"));
const Checkout = lazy(() => import("./routes/Checkout"));
const OrderSuccess = lazy(() => import("./routes/OrderSuccess"));
const NotFound = lazy(() => import("./routes/NotFound"));

const Admin = lazy(() => import("./routes/admin/Admin"));
const AdminLogin = lazy(() => import("./routes/admin/AdminLogin"));

/**
 * Every route change starts at the top.
 *
 * Three things were getting in the way:
 *  - the browser restores the previous scroll position on navigation, which
 *    fires after our own scroll, so it has to be switched off;
 *  - lazy routes mount a frame later, so a single scroll can land before the
 *    new page has any height and be clamped back;
 *  - ScrollTrigger caches page height and needs telling the page changed.
 * An in-page anchor like /#studio is left alone.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  React.useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  React.useLayoutEffect(() => {
    if (hash) return;

    window.scrollTo(0, 0);

    const raf = requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      ScrollTrigger.refresh();
    });

    return () => cancelAnimationFrame(raf);
  }, [pathname, hash]);

  return null;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <LanguageProvider>
        <GlobalStyles />
        <AuthProvider>
          <CartProvider>
            <ScrollToTop />
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/produktet" element={<Products />} />
                <Route path="/produktet/:id" element={<ProductDetail />} />
                <Route path="/rreth-nesh" element={<About />} />
                <Route path="/kontakt" element={<Contact />} />
                <Route path="/porosia" element={<Checkout />} />
                <Route
                  path="/porosia/sukses/:orderId"
                  element={<OrderSuccess />}
                />

                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/*" element={<Admin />} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </CartProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
